import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useCallback, useEffect, useRef, useState } from 'react';
import { defaultReducer } from '../../core/glue';
import runtimeVar from '../../core/runtimeVar';
import subscribe from '../../core/subscribe';
import { isArray, isModel } from '../../tools';
var useDerivedStateWithModel = function useDerivedStateWithModel(model, callback, deps) {
  var callWhenInitial = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;
  var keepSilent = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;
  var modelRef = useRef();
  var noticeChangeRef = useRef(model());
  var unsubCallbackRef = useRef();
  var keepSilentRef = useRef(keepSilent);
  keepSilentRef.current = keepSilent;
  var _useState = useState({}),
    _useState2 = _slicedToArray(_useState, 2),
    refresh = _useState2[1];
  var subscribeCallback = useCallback(function (s) {
    noticeChangeRef.current = s;
    if (keepSilentRef.current) {
      return;
    }
    refresh({});
  }, []);
  if (modelRef.current !== model) {
    var _unsubCallbackRef$cur;
    (_unsubCallbackRef$cur = unsubCallbackRef.current) === null || _unsubCallbackRef$cur === void 0 ? void 0 : _unsubCallbackRef$cur.call(unsubCallbackRef);
    unsubCallbackRef.current = subscribe([model], subscribeCallback, false, true);
  }
  modelRef.current = model;
  var updateState = useCallback(function (s) {
    var _modelRef$current;
    var silent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    if (!silent) {
      refresh({});
    }
    runtimeVar.runtimeFromDerived = true;
    noticeChangeRef.current = (_modelRef$current = modelRef.current) === null || _modelRef$current === void 0 ? void 0 : _modelRef$current.preTreat(s);
    if (keepSilentRef.current) {
      var _modelRef$current2;
      runtimeVar.runtimeNoPreTreat = true;
      var _result = noticeChangeRef.current;
      if (typeof _result === 'function') {
        _result = function result() {
          return _result;
        };
      }
      (_modelRef$current2 = modelRef.current) === null || _modelRef$current2 === void 0 ? void 0 : _modelRef$current2.silent(_result);
      runtimeVar.runtimeFromDerived = false;
      return;
    }
    runtimeVar.runtimeFromDerived = false;
  }, []);
  var callWhenChange = function callWhenChange() {
    var silent = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    updateState(callback(modelRef.current()), silent);
  };
  var _useState3 = useState(function () {
      return new Map();
    }),
    _useState4 = _slicedToArray(_useState3, 1),
    map = _useState4[0];
  var analyzeDeps = function analyzeDeps(ds) {
    if (!isArray(ds)) return;
    for (var i = 0; i < ds.length; i += 1) {
      var d = ds[i];
      if (isModel(d)) {
        if (map.has(d)) {
          map.get(d)();
        }
        map.set(d, d.onChange(function () {
          callWhenChange(false);
        }));
      }
    }
  };
  var _useState5 = useState(function () {
      if (callWhenInitial) {
        callWhenChange();
      }
      analyzeDeps(deps);
      return {
        current: deps
      };
    }),
    _useState6 = _slicedToArray(_useState5, 1),
    cachedDeps = _useState6[0];
  if (deps.length !== cachedDeps.current.length) {
    cachedDeps.current = deps;
    callWhenChange();
    analyzeDeps(deps);
  } else {
    for (var i = 0; i < deps.length; i += 1) {
      if (!Object.is(deps[i], cachedDeps.current[i])) {
        cachedDeps.current = deps;
        callWhenChange();
        analyzeDeps(deps);
        break;
      }
    }
  }
  useEffect(function () {
    return function () {
      var unsubscribes = Array.from(map.values());
      for (var _i = 0; _i < unsubscribes.length; _i += 1) {
        unsubscribes[_i]();
      }
      map.clear();
    };
  }, []);
  useEffect(function () {
    if (keepSilentRef.current) return;
    var _result2 = noticeChangeRef.current;
    if (typeof _result2 === 'function') {
      _result2 = function result() {
        return _result2;
      };
    }
    runtimeVar.runtimeFromDerived = true;
    modelRef.current.race(_result2, defaultReducer, subscribeCallback);
    runtimeVar.runtimeFromDerived = false;
  }, [noticeChangeRef.current]);
  return [keepSilentRef.current ? modelRef.current() : noticeChangeRef.current];
};
export default useDerivedStateWithModel;